import React, { useState } from 'react';
import {
  Paper,
  FormControl,
  Select,
  MenuItem,
  Alert,
  SelectChangeEvent
} from '@mui/material';
import { ProfileData, FormatType } from '../types';
import MarkdownEditor from '../../../components/editor/MarkdownEditor';
import { useProfileEditor } from '../hooks/useProfileEditor';
import { useEditorShortcuts } from '../hooks/useEditorShortcuts';
import { ProposalService } from '../services/proposalService';
import { useAuth } from '../../../contexts/AuthContext';
import { markdownToProfile } from '../utils/markdownTransform';
import { generateProposalFilename } from '../../../utils/fileNaming';

interface ProfileEditorProps {
  profiles: ProfileData[];
  currentIndex: number;
  formatType: FormatType;
  clientName?: string;
  onProfileChange: (index: number, profile: ProfileData) => void;
  onNavigate: (index: number) => void;
  onFormatChange?: (format: FormatType) => void;
  showFormatToggle?: boolean;
  onExport?: (currentProfileData?: ProfileData) => void;
}

const ProfileEditor: React.FC<ProfileEditorProps> = ({
  profiles,
  currentIndex,
  formatType,
  clientName,
  onProfileChange,
  onNavigate,
  onFormatChange,
  onExport
}) => {
  const currentProfile = profiles[currentIndex];
  const { getAccessToken } = useAuth();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  
  // Use our enhanced editor hooks
  const {
    currentMarkdown,
    isDirty,
    canUndo,
    canRedo,
    undo,
    redo,
    handleMarkdownChange,
    handleNavigation,
    saveNow
  } = useProfileEditor({
    profiles,
    currentIndex,
    formatType,
    onProfileChange,
    onNavigate,
    onFormatChange: onFormatChange || (() => {})
  });

  // Keyboard shortcuts
  useEditorShortcuts({
    onSave: saveNow,
    onUndo: undo,
    onRedo: redo,
    onNavigateNext: () => handleNavigation(Math.min(currentIndex + 1, profiles.length - 1)),
    onNavigatePrev: () => handleNavigation(Math.max(currentIndex - 1, 0)),
    canUndo,
    canRedo,
    canNavigateNext: currentIndex < profiles.length - 1,
    canNavigatePrev: currentIndex > 0,
    canSave: isDirty
  });

  // Handle navigation
  const handleNavigate = (direction: 'first' | 'prev' | 'next' | 'last') => {
    switch (direction) {
      case 'first':
        handleNavigation(0);
        break;
      case 'prev':
        if (currentIndex > 0) handleNavigation(currentIndex - 1);
        break;
      case 'next':
        if (currentIndex < profiles.length - 1) handleNavigation(currentIndex + 1);
        break;
      case 'last':
        handleNavigation(profiles.length - 1);
        break;
    }
  };

  // Handle dropdown navigation
  const handleDropdownChange = (event: SelectChangeEvent<number>) => {
    handleNavigation(Number(event.target.value));
  };


  // Handle PowerPoint export
  const handleExportToPowerPoint = async () => {
    // If onExport prop is provided, use it (for viewer integration)
    if (onExport) {
      // Convert current markdown to profile data and pass it directly to the export handler
      if (profiles[currentIndex]) {
        const updatedProfile = markdownToProfile(currentMarkdown, formatType, profiles[currentIndex]);
        // Also update the parent state
        onProfileChange(currentIndex, updatedProfile);
        // Pass the updated profile directly to avoid state update timing issues
        onExport(updatedProfile);
      } else {
        onExport();
      }
      return;
    }

    // Otherwise use the legacy direct download behavior
    const token = await getAccessToken();
    if (!token) {
      setExportError('Authentication required for export');
      return;
    }

    if (profiles.length === 0) {
      setExportError('No profiles to export');
      return;
    }

    // Only export PowerPoint for one-slider and intro formats
    if (formatType === 'full' || formatType === 'web-bio') {
      setExportError('PowerPoint export is only available for one-slider and intro formats. Please use PDF export for this format.');
      return;
    }

    try {
      setIsExporting(true);
      setExportError(null);

      const proposalService = new ProposalService(token);
      
      // Generate standardized filename
      // Extract first and last name from the profile's full name
      const nameParts = currentProfile?.name ? currentProfile.name.trim().split(/\s+/) : [];
      const firstName = nameParts.length > 0 ? nameParts[0] : undefined;
      const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : undefined;

      const filename = generateProposalFilename({
        clientName: clientName || 'Proposal',
        firstName,
        lastName,
        format: 'pptx'
      });

      // Ensure current editor content is in the profiles
      const updatedProfiles = [...profiles];
      if (profiles[currentIndex]) {
        updatedProfiles[currentIndex] = markdownToProfile(currentMarkdown, formatType, profiles[currentIndex]);
      }

      // Export all profiles to PowerPoint
      const blob = await proposalService.exportProposal(
        `powerpoint_${Date.now()}`, // Generate a temporary proposal ID
        'pptx',
        updatedProfiles
      );

      // Download the file
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error('Export failed:', error);
      setExportError(error instanceof Error ? error.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  };

  if (!currentProfile) {
    return (
      <Paper elevation={2} sx={{ p: 3 }}>
        <Alert severity="info">
          No profiles available for editing. Please generate profiles first.
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      {/* Clean single-line header with user selector, save, and export */}
      <div className="panel-header">
        <div className="panel-title">
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button 
              className="control-btn"
              onClick={() => handleNavigate('prev')}
              disabled={currentIndex === 0}
              title="Previous profile"
              style={{ minWidth: '32px' }}
            >
              <span className="material-icons">chevron_left</span>
            </button>
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={currentIndex}
                onChange={handleDropdownChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': {
                    padding: '8px 32px 8px 12px',
                    color: 'rgba(255,255,255,0.9)',
                    fontSize: '16px',
                    fontWeight: 500
                  }
                }}
              >
                {profiles.map((profile, index) => (
                  <MenuItem key={index} value={index}>
                    {profile.name} ({index + 1} of {profiles.length})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <button 
              className="control-btn"
              onClick={() => handleNavigate('next')}
              disabled={currentIndex === profiles.length - 1}
              title="Next profile"
              style={{ minWidth: '32px' }}
            >
              <span className="material-icons">chevron_right</span>
            </button>
          </div>
        </div>
        <div className="panel-controls">
          <button 
            className="control-btn" 
            title="Save"
            onClick={saveNow}
            disabled={!isDirty}
          >
            <span className="material-icons">{isDirty ? 'save' : 'check'}</span>
          </button>
          <button
            className="export-btn"
            onClick={handleExportToPowerPoint}
            disabled={isExporting || profiles.length === 0}
            title={formatType === 'full' || formatType === 'web-bio' ? 'Export as PDF' : 'Export as PowerPoint'}
          >
            <span className="material-icons">{formatType === 'full' || formatType === 'web-bio' ? 'picture_as_pdf' : 'slideshow'}</span>
            {isExporting ? 'Exporting...' : 'Export'}
          </button>
        </div>
      </div>
      
      {/* Editor content - clean like Enhancer */}
      <div className="panel-content" style={{ position: 'relative' }}>
        {exportError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setExportError(null)}>
            {exportError}
          </Alert>
        )}
        <MarkdownEditor
          value={currentMarkdown}
          onChange={handleMarkdownChange}
          height={500}
          placeholder="Edit profile content..."
        />
      </div>
    </>
  );
};

export default ProfileEditor;